import { zodResolver } from '@hookform/resolvers/zod';
import {
  contatoSchema,
  CONTATO_PADRAO,
  redeSocialSchema,
  type Contato,
  type Coordenadas,
} from '@plane/shared';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Field } from '@/components/field';
import { BuscadorDeMapa } from '@/modules/site-content/components/buscador-de-mapa';
import { useContato, useSaveContato } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

/**
 * Formato do formulário.
 *
 * As coordenadas saem do objeto aninhado e viram três campos planos: um
 * interruptor e dois números. É o que permite o estado "sem mapa" existir na
 * tela — um par de campos vazios seria ambíguo, e o contrato exige os dois ou
 * nenhum.
 */
const formularioSchema = contatoSchema
  .omit({ coordenadas: true })
  .extend({
    comMapa: z.boolean(),
    lat: z.number().min(-90).max(90).nullable(),
    lon: z.number().min(-180).max(180).nullable(),
    redes: z.array(redeSocialSchema).max(6),
  })
  .refine((valores) => !valores.comMapa || (valores.lat !== null && valores.lon !== null), {
    message: 'Informe latitude e longitude, ou desligue o mapa.',
    path: ['lat'],
  });

type Formulario = z.infer<typeof formularioSchema>;

function paraFormulario(contato: Contato): Formulario {
  const { coordenadas, ...resto } = contato;

  return {
    ...resto,
    comMapa: coordenadas !== null,
    lat: coordenadas?.lat ?? null,
    lon: coordenadas?.lon ?? null,
  };
}

function paraContato(valores: Formulario): Contato {
  const { comMapa, lat, lon, ...resto } = valores;

  return {
    ...resto,
    coordenadas: comMapa && lat !== null && lon !== null ? { lat, lon } : null,
  };
}

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

/**
 * Campo numérico que aceita ficar vazio.
 *
 * Existe porque o `valueAsNumber` do React Hook Form devolve `NaN` quando o
 * campo é limpo, e `NaN` passa por `z.number()` sem reclamar — gravaria
 * coordenada inválida.
 *
 * Recebe `unknown`, e não `string`, porque o `setValueAs` não é chamado só com
 * o que a pessoa digita: ele também roda sobre o **valor padrão** quando o
 * campo é montado, e ali o valor já é `number` ou `null`. Tipar como `string`
 * compila, e quebra a tela ao abrir. É a segunda vez que esta armadilha aparece
 * neste painel — ver `medidaOuNulo`, em `modules/projects/medida.ts`.
 */
function numeroOuNulo(valor: unknown): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== 'string') return null;

  const limpo = valor.trim();

  if (limpo === '') return null;

  const numero = Number(limpo.replace(',', '.'));

  return Number.isFinite(numero) ? numero : null;
}

export function ContatoPage() {
  const consulta = useContato();
  const salvar = useSaveContato();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      // `key` garante formulário novo quando o conteúdo salvo chega, em vez de
      // um `reset` manual que é fácil de esquecer.
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? CONTATO_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(contato) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(contato)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Contato;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (contato: Contato) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Formulario>({
    resolver: zodResolver(formularioSchema),
    defaultValues: paraFormulario(inicial),
  });

  const redes = useFieldArray({ control, name: 'redes' });
  const comMapa = watch('comMapa');
  const lat = watch('lat');
  const lon = watch('lon');

  // A busca usa as duas linhas do endereço, que juntas trazem rua, número,
  // cidade, estado e CEP — é o que o serviço de mapas precisa para acertar.
  const consultaDoEndereco = [watch('enderecoLinha1'), watch('enderecoLinha2')]
    .map((linha) => linha.trim())
    .filter((linha) => linha !== '')
    .join(', ');

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit((valores) => aoEnviar(paraContato(valores)))(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Empresa e contato</h1>
        <p className="mt-3 text-sm text-ink-soft">
          Aparece no rodapé de todas as páginas e na seção do showroom.
        </p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os campos abaixo estão com o conteúdo de demonstração do
          protótipo — <strong>confira um a um</strong> antes de salvar, porque o telefone e o e-mail
          já aparecem no site.
        </p>
      )}

      <Field
        id="empresa"
        label="Nome da empresa"
        error={errors.empresa?.message}
        {...register('empresa')}
      />

      <Grupo titulo="Telefone fixo">
        <Field
          id="telefoneExibicao"
          label="Como aparece no site"
          placeholder="(45) 3000-1420"
          error={errors.telefoneExibicao?.message}
          {...register('telefoneExibicao')}
        />
        <Field
          id="telefoneDigitos"
          label="Só dígitos, com DDI"
          placeholder="554530001420"
          inputMode="numeric"
          error={errors.telefoneDigitos?.message}
          {...register('telefoneDigitos')}
        />
      </Grupo>

      <Grupo
        titulo="WhatsApp"
        nota="Costuma ser um celular diferente do fixo. É este número que o botão de WhatsApp abre — se puser o fixo aqui, a conversa não chega em ninguém."
      >
        <Field
          id="whatsappExibicao"
          label="Como aparece no site"
          placeholder="(45) 99000-1420"
          error={errors.whatsappExibicao?.message}
          {...register('whatsappExibicao')}
        />
        <Field
          id="whatsappDigitos"
          label="Só dígitos, com DDI"
          placeholder="5545990001420"
          inputMode="numeric"
          error={errors.whatsappDigitos?.message}
          {...register('whatsappDigitos')}
        />
      </Grupo>

      <Field
        id="email"
        label="E-mail"
        type="email"
        error={errors.email?.message}
        {...register('email')}
      />

      <Grupo titulo="Endereço">
        <Field
          id="enderecoLinha1"
          label="Rua e número"
          error={errors.enderecoLinha1?.message}
          {...register('enderecoLinha1')}
        />
        <Field
          id="enderecoLinha2"
          label="Cidade, estado e CEP"
          error={errors.enderecoLinha2?.message}
          {...register('enderecoLinha2')}
        />
      </Grupo>

      <Grupo titulo="Atendimento" nota="Por extenso no showroom; abreviado na coluna do rodapé.">
        <Field
          id="horarioSemana"
          label="Semana — por extenso"
          error={errors.horarioSemana?.message}
          {...register('horarioSemana')}
        />
        <Field
          id="horarioSabado"
          label="Sábado — por extenso"
          error={errors.horarioSabado?.message}
          {...register('horarioSabado')}
        />
        <Field
          id="horarioSemanaCurto"
          label="Semana — abreviado"
          error={errors.horarioSemanaCurto?.message}
          {...register('horarioSemanaCurto')}
        />
        <Field
          id="horarioSabadoCurto"
          label="Sábado — abreviado"
          error={errors.horarioSabadoCurto?.message}
          {...register('horarioSabadoCurto')}
        />
      </Grupo>
      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">Mapa</legend>

        <label className="mt-2 flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('comMapa')} />
          Mostrar o mapa e o botão de rota no site
        </label>

        {comMapa && (
          <div className="mt-4 flex flex-col gap-4">
            <BuscadorDeMapa
              consulta={consultaDoEndereco}
              coordenadas={lat !== null && lon !== null ? { lat, lon } : null}
              aoConfirmar={(encontrado: Coordenadas) => {
                setValue('lat', encontrado.lat, { shouldValidate: true });
                setValue('lon', encontrado.lon, { shouldValidate: true });
              }}
            />

            {errors.lat && (
              <p role="alert" className="text-xs text-brass">
                {errors.lat.message}
              </p>
            )}

            {/*
              As coordenadas continuam editáveis, escondidas atrás de um
              detalhe. Quem tiver o ponto exato — tirado do celular na porta da
              loja, por exemplo — acerta melhor que qualquer busca por endereço,
              e tirar essa saída seria empobrecer a ferramenta para simplificar
              a tela.
            */}
            <details>
              <summary className="cursor-pointer text-sm text-brand-700 underline">
                Digitar as coordenadas à mão
              </summary>

              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field
                  id="lat"
                  label="Latitude"
                  placeholder="-24.9555"
                  inputMode="decimal"
                  error={errors.lat?.message}
                  {...register('lat', { setValueAs: numeroOuNulo })}
                />
                <Field
                  id="lon"
                  label="Longitude"
                  placeholder="-53.4552"
                  inputMode="decimal"
                  error={errors.lon?.message}
                  {...register('lon', { setValueAs: numeroOuNulo })}
                />
              </div>
            </details>
          </div>
        )}
      </fieldset>

      <Field id="cnpj" label="CNPJ" error={errors.cnpj?.message} {...register('cnpj')} />

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">Redes sociais</legend>

        <ul className="mt-3 flex list-none flex-col gap-4 p-0">
          {redes.fields.map((campo, indice) => (
            <li key={campo.id} className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
              <Field
                id={`rede-rotulo-${indice}`}
                label="Nome"
                error={errors.redes?.[indice]?.rotulo?.message}
                {...register(`redes.${indice}.rotulo`)}
              />
              <Field
                id={`rede-url-${indice}`}
                label="Endereço"
                placeholder="https://instagram.com/planemoveis"
                error={errors.redes?.[indice]?.url?.message}
                {...register(`redes.${indice}.url`)}
              />
              <button
                type="button"
                onClick={() => redes.remove(indice)}
                className="self-start text-xs text-brass underline sm:self-auto sm:pb-3"
              >
                Remover
              </button>
            </li>
          ))}
        </ul>

        {redes.fields.length < 6 && (
          <button
            type="button"
            onClick={() => redes.append({ rotulo: '', url: '' })}
            className="mt-3 text-sm text-brand-700 underline"
          >
            Adicionar rede
          </button>
        )}
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}

function Grupo({
  titulo,
  nota,
  children,
}: {
  titulo: string;
  nota?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">{titulo}</legend>
      {nota && <p className="mt-1.5 text-xs text-ink-soft">{nota}</p>}
      <div className="mt-3 grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
